import type { MedplumClient } from "@medplum/core";
import type { DocumentReference, Extension as FhirExtension } from "@medplum/fhirtypes";
import * as Sentry from "@sentry/nextjs";
import type {
  CreateResourceIfNoneExistParams,
  CreateResourceOptions,
} from "@vibrant/models/fhir/FhirResource";
import type { ResourceRef, SystemValuePair } from "@vibrant/models/fhir/fhir-resource-types";
import { hashFileContent, hashFileContentBase64 } from "@/lib/crypto/hash-file-content";
import { resolveBinaryIdFromUrl } from "@/lib/files/utils";
import type { Logger } from "@/lib/logger";
import { extractTelemetryErrorMetadata } from "@/lib/telemetry-utils";
import { time } from "@/lib/time";
import type { Document } from "./Document";
import { MedplumFile } from "./MedplumFile";

type DocumentReferenceCreatePayload = Omit<DocumentReference, "identifier" | "resourceType"> & {
  identifier?: SystemValuePair[];
};

export interface DocumentFileInput {
  content: Buffer | Uint8Array;
  contentType?: string;
  filename: string;
  title?: string;
  createdAt?: string | Date;
}

export interface CreateDocumentFromFileRequest {
  subject: ResourceRef | string;
  custodian?: ResourceRef | string;
  practiceRef?: ResourceRef | string;
  securityContext?: ResourceRef | string;
  description?: string;
  date?: string;
  identifiers?: SystemValuePair[];
  extensions?: FhirExtension[];
  /** Optional authorship attribution for the document (e.g., originating lab/vendor org). */
  author?: DocumentReference["author"];
  /** Optional metadata including tags for quarantine/dataset tracking. */
  meta?: DocumentReference["meta"];
  /** Optional document category codings for classification. */
  category?: DocumentReference["category"];
  /** Optional document type coding for downstream classification and rendering. */
  type?: DocumentReference["type"];
  /** Optional linkage to related resources such as the originating order. */
  context?: DocumentReference["context"];
  /** Optional document lineage (e.g. `transforms` for derived artifacts). */
  relatesTo?: DocumentReference["relatesTo"];
  file: DocumentFileInput;
}

export interface CreateDocumentFromFileOptions extends CreateResourceOptions {
  ifNoneExistQuery?: string;
  telemetry?: DocumentWriteTelemetry;
  /**
   * Opt in to content-hash dedup. When set, the write attaches a canonical dedup-key identifier
   * and derives an `ifNoneExistQuery` from it (unless the caller passed one), so re-running the
   * same import can't create duplicate DocumentReferences (ENG-4700). Intended for migration/import
   * paths. Runtime flows that manage their own dedup (e.g. Vital lab documents keyed on the order
   * identifier) should leave this off so their behavior is unchanged.
   */
  dedupeByContentHash?: boolean;
}

/**
 * Canonical identifier system for a document's dedup key, attached when a caller opts in via
 * `dedupeByContentHash`. The key is `<subjectId>-<sha256-of-bytes>` plus, when present, a short
 * hash of the document's linkage (`context.related` refs + `type`). Folding linkage in means
 * byte-identical PDFs that belong to different contexts — e.g. a lab requisition template reused
 * across two orders — are NOT collapsed, while re-importing the *same* source document still dedups.
 * Keeping the system canonical (rather than per-importer) means any opted-in uploader dedups
 * against any other.
 */
export const DOCUMENT_DEDUP_KEY_SYSTEM = "vibrant.document.dedup-key";

/**
 * Telemetry-context keys promoted to Sentry tags on a cleanup failure, so an orphaned Binary is
 * searchable by the scope that produced it. Canonical logging fields (root `AGENTS.md`) plus the
 * two ids that identify the write itself. Callers that don't supply one simply don't get the tag.
 */
const SENTRY_TAGGED_FIELDS = [
  "memberId",
  "providerId",
  "practiceId",
  "encounterId",
  "appointmentId",
  "keyId",
  "documentReferenceId",
] as const;

export interface CreateDocumentFromFileResult {
  document: Document;
  wasCreated: boolean;
}

export interface DocumentWriteTelemetry {
  context?: Record<string, unknown>;
  logger?: Pick<Logger, "error" | "warn">;
  onStage?: (event: DocumentWriteTelemetryStageEvent) => void;
}

export interface DocumentWriteTelemetryStageEvent {
  binaryId?: string;
  stage: "write.binary" | "write.cleanup" | "write.document";
}

interface CreateDocumentFromFileInput {
  documentModel: DocumentModelAdapter;
  medplum: MedplumClient;
  options?: CreateDocumentFromFileOptions;
  request: CreateDocumentFromFileRequest;
}

export interface DocumentModelAdapter {
  createDocument: (resource: DocumentReference) => Document;
  createResource: (
    medplum: MedplumClient,
    resource: DocumentReferenceCreatePayload,
    options?: CreateResourceOptions,
  ) => Promise<DocumentReference>;
  createResourceIfNoneExist: (
    medplum: MedplumClient,
    params: CreateResourceIfNoneExistParams<DocumentReferenceCreatePayload>,
  ) => Promise<DocumentReference>;
}

/**
 * Medplum `_sort=period` uses `DocumentReference.context.period`. When callers set `date` but omit
 * `context.period`, sorting is unstable. Merge `period.start` from `date` when `start` is absent.
 */
export function withDocumentReferenceSortPeriod<
  T extends {
    date?: string;
    context?: DocumentReference["context"];
  },
>(payload: T): T {
  const dateValue = payload.date;
  if (!dateValue) {
    return payload;
  }
  if (payload.context?.period?.start) {
    return payload;
  }
  return {
    ...payload,
    context: {
      ...payload.context,
      period: {
        ...payload.context?.period,
        start: dateValue,
      },
    },
  };
}

/**
 * Creates a DocumentReference from raw file bytes while keeping Binary cleanup idempotent.
 *
 * When `ifNoneExistQuery` is set, performs the same lookup Medplum would use for conditional
 * create (`searchOne` on `DocumentReference` with that query). If a match exists, returns it
 * immediately without uploading bytes or creating a new resource.
 */
export async function createDocumentFromFile({
  documentModel,
  medplum,
  request,
  options,
}: CreateDocumentFromFileInput): Promise<CreateDocumentFromFileResult> {
  const { file, identifiers, extensions, meta, category, type, context, relatesTo } = request;
  const telemetry = options?.telemetry;
  const telemetryLogger = telemetry?.logger;
  const subject = toReference(request.subject);

  // Content-hash dedup is opt-in via `dedupeByContentHash` (migration/import paths). When on, attach
  // a canonical dedup-key identifier and derive the conditional-create query from it so re-imports
  // can't silently create duplicate DocumentReferences (ENG-4700). The key folds in linkage
  // (context.related + type) so byte-identical PDFs for different contexts aren't collapsed. Runtime
  // flows that manage their own dedup leave this off, keeping their behavior unchanged.
  const dedupKeyIdentifier = options?.dedupeByContentHash
    ? buildDedupKeyIdentifier(subject, file.content, request)
    : undefined;
  const documentIdentifiers = dedupKeyIdentifier
    ? mergeDedupKeyIdentifier(identifiers, dedupKeyIdentifier)
    : identifiers;
  const ifNoneExistQuery =
    options?.ifNoneExistQuery ??
    (dedupKeyIdentifier
      ? `identifier=${DOCUMENT_DEDUP_KEY_SYSTEM}|${dedupKeyIdentifier.value}`
      : undefined);

  if (ifNoneExistQuery) {
    const existing = await medplum.searchOne("DocumentReference", ifNoneExistQuery);
    if (existing) {
      return {
        document: documentModel.createDocument(existing),
        wasCreated: false,
      };
    }
  }

  const contentType = file.contentType ?? "application/octet-stream";
  const createdAt = file.createdAt ? time(file.createdAt).toISOString() : time().toISOString();
  const uploadContent = normalizeToUint8Array(file.content);
  const uploadTelemetry = {
    ...(telemetryLogger ? { logger: telemetryLogger } : {}),
    context: getDocumentWriteTelemetryContext({
      telemetry,
      context: {
        stage: "write.binary",
      },
    }),
  };
  const securityContext = getBinarySecurityContext({
    securityContext: request.securityContext,
    subject,
  });
  const practiceRef = request.practiceRef ?? options?.practiceOrganization;

  telemetry?.onStage?.({ stage: "write.binary" });
  const uploadedFile = await MedplumFile.upload(medplum, {
    data: uploadContent,
    contentType,
    filename: file.filename,
    practiceRef: practiceRef ? toReference(practiceRef) : undefined,
    securityContext,
    telemetry: uploadTelemetry,
  });
  telemetry?.onStage?.({ binaryId: uploadedFile.id, stage: "write.binary" });

  // Content fingerprint (base64 SHA-256) — best-effort. The Binary is already stored by this point,
  // so a hashing failure must never block the document write; on error we store without the hash.
  let contentHash: string | undefined;
  try {
    contentHash = hashFileContentBase64(file.content);
  } catch {
    telemetryLogger?.warn("document upload: content hash failed; storing attachment without hash", {
      stage: "write.document",
    });
  }

  const documentReferencePayload = withDocumentReferenceSortPeriod({
    status: "current" as const,
    docStatus: "final" as const,
    description: request.description ?? file.title ?? file.filename,
    date: request.date ?? createdAt,
    subject,
    custodian: request.custodian ? toReference(request.custodian) : undefined,
    author: request.author,
    type,
    context,
    content: [
      {
        attachment: {
          contentType,
          url: uploadedFile.url ?? `Binary/${uploadedFile.id}`,
          title: file.title ?? file.filename,
          creation: createdAt,
          // Content fingerprint + byte size, stored so documents are identifiable/dedupable and
          // integrity-checkable even after the Binary is deleted. `hash` is best-effort (see above).
          hash: contentHash,
          size: uploadContent.byteLength,
        },
      },
    ],
    identifier: documentIdentifiers,
    extension: extensions,
    meta,
    category,
    relatesTo,
  });

  const uploadedBinaryId = uploadedFile.id;

  telemetry?.onStage?.({ binaryId: uploadedFile.id, stage: "write.document" });
  const documentReference = await createDocumentReference({
    documentModel,
    medplum,
    documentReferencePayload,
    ifNoneExistQuery,
    options,
    telemetry,
    uploadedBinaryId,
  });

  // Generally, if the code is able to make it past the first ifNoneExist check then we are pretty
  // safe to assume that the document was created, rather than loaded. (Atleast at the current levels of traffic)
  // However, we will still try to infer based on whether the binary attached to the document
  // is the same as the binary we created in this function
  // If they are not the same, then we actually returned an already created binary.
  // And that means the binary we made in this function is orphaned, and ought to be deleted.
  const documentPrimaryBinaryId = resolveBinaryIdFromUrl(
    documentReference.content?.[0]?.attachment?.url ?? "",
  );
  const wasCreated = ifNoneExistQuery
    ? Boolean(
        uploadedBinaryId && documentPrimaryBinaryId && documentPrimaryBinaryId === uploadedBinaryId,
      )
    : true;

  if (!wasCreated && uploadedBinaryId) {
    // The conditional create returned a pre-existing document (a concurrent create won, or an
    // idempotent retry landed): the Binary we just uploaded is unreferenced. Delete it so races
    // and retries don't leak orphaned Binaries. Best-effort - a delete failure logs but never
    // fails the write, since the document itself is correct either way.
    telemetryLogger?.warn(
      "Conditional create returned existing document; deleting orphaned uploaded Binary",
      getDocumentWriteTelemetryContext({
        telemetry,
        context: {
          documentReferenceId: documentReference.id,
          uploadedBinaryId,
          documentPrimaryBinaryId,
          stage: "write.document",
        },
      }),
    );
    await cleanupUploadedBinary({
      medplum,
      binaryId: uploadedBinaryId,
      telemetry,
      ...(telemetryLogger ? { logger: telemetryLogger } : {}),
      message: "Failed to delete orphaned uploaded Binary after conditional create",
    });
  }

  return {
    document: documentModel.createDocument(documentReference),
    wasCreated,
  };
}

/**
 * Creates the DocumentReference and deletes the uploaded Binary if creation fails.
 */
async function createDocumentReference({
  documentModel,
  medplum,
  documentReferencePayload,
  ifNoneExistQuery,
  options,
  telemetry,
  uploadedBinaryId,
}: {
  documentModel: DocumentModelAdapter;
  documentReferencePayload: DocumentReferenceCreatePayload;
  ifNoneExistQuery?: string;
  medplum: MedplumClient;
  options?: CreateResourceOptions;
  telemetry?: DocumentWriteTelemetry;
  uploadedBinaryId?: string;
}): Promise<DocumentReference> {
  try {
    return ifNoneExistQuery
      ? await documentModel.createResourceIfNoneExist(medplum, {
          resource: documentReferencePayload,
          ifNoneExistQuery,
          options,
        })
      : await documentModel.createResource(medplum, documentReferencePayload, options);
  } catch (error) {
    telemetry?.logger?.error(
      "Failed to create DocumentReference from uploaded file",
      getDocumentWriteTelemetryContext({
        telemetry,
        context: {
          ...extractTelemetryErrorMetadata({ error }),
          binaryId: uploadedBinaryId,
          stage: "write.document",
        },
      }),
    );
    if (uploadedBinaryId) {
      await cleanupUploadedBinary({
        medplum,
        binaryId: uploadedBinaryId,
        telemetry,
        ...(telemetry?.logger ? { logger: telemetry.logger } : {}),
        message: "Failed to delete uploaded binary after document creation failure",
      });
    }
    throw error;
  }
}

/**
 * Deletes an uploaded Binary after a failed or duplicate DocumentReference write.
 *
 * A failure here leaves PHI bytes on the server with nothing referencing them, so it is an
 * `error` with a Sentry event rather than a `warn`: it needs an operator to clean up, and it
 * can be structural rather than intermittent. A caller whose client is denied Binary `delete`
 * fails here on every attempt, and Binary `delete` cannot always be granted to fix it - Binary
 * has no compartment or search params, so any grant past `create` is project-wide (the API v2
 * serving policy is deliberately create-only for exactly that reason; see
 * `apps/web/lib/api/v2/AGENTS.md`). A write path that can't delete has to avoid producing the
 * orphan instead.
 */
async function cleanupUploadedBinary({
  medplum,
  binaryId,
  telemetry,
  logger,
  message,
}: {
  binaryId: string;
  logger?: Pick<Logger, "error">;
  medplum: MedplumClient;
  message: string;
  telemetry?: DocumentWriteTelemetry;
}): Promise<void> {
  try {
    await MedplumFile.delete(medplum, binaryId);
  } catch (error) {
    const context = getDocumentWriteTelemetryContext({
      telemetry,
      context: {
        ...extractTelemetryErrorMetadata({ error }),
        binaryId,
        outcome: "cleanup_failed",
        stage: "write.cleanup",
      },
    });
    logger?.error(message, context);
    Sentry.captureException(error, {
      tags: getSentryTags(context),
      extra: { binaryId, stage: "write.cleanup" },
    });
  }
}

/** Canonical ids the caller supplied on telemetry context, as searchable Sentry tags. */
function getSentryTags(context: Record<string, unknown>): Record<string, string> {
  const tags: Record<string, string> = { outcome: "cleanup_failed" };
  for (const field of SENTRY_TAGGED_FIELDS) {
    const value = context[field];
    if (typeof value === "string") tags[field] = value;
  }
  return tags;
}

/**
 * Merges caller and stage-specific telemetry without leaking file metadata.
 */
function getDocumentWriteTelemetryContext({
  telemetry,
  context,
}: {
  context?: Record<string, unknown>;
  telemetry?: DocumentWriteTelemetry;
}): Record<string, unknown> {
  return {
    ...telemetry?.context,
    ...context,
  };
}

function normalizeToUint8Array(content: Buffer | Uint8Array): Uint8Array {
  return new Uint8Array(content.buffer, content.byteOffset, content.byteLength);
}

/**
 * Uses the patient subject as Binary security context unless a caller provides a stricter anchor.
 */
function getBinarySecurityContext({
  securityContext,
  subject,
}: {
  securityContext?: ResourceRef | string;
  subject: ResourceRef;
}): ResourceRef | undefined {
  if (securityContext) {
    return toReference(securityContext);
  }

  return subject.reference.startsWith("Patient/") ? subject : undefined;
}

/**
 * Builds the canonical dedup-key identifier: subject + content hash, plus a short hash of the
 * document's linkage (`context.related` refs + `type`) when present. Scoping to the subject keeps
 * identical bytes for different patients apart; folding in linkage keeps byte-identical documents
 * that belong to different contexts (e.g. a requisition template reused across two lab orders)
 * apart, while a re-import of the same source document still produces the same key and dedups.
 */
function buildDedupKeyIdentifier(
  subject: ResourceRef,
  content: Buffer | Uint8Array,
  request: CreateDocumentFromFileRequest,
): SystemValuePair {
  const scopeId = subject.reference.split("/").pop() || subject.reference;
  const base = `${scopeId}-${hashFileContent(content)}`;
  const linkage = getDedupLinkageDiscriminator(request);
  const value = linkage ? `${base}-${linkage}` : base;
  return { system: DOCUMENT_DEDUP_KEY_SYSTEM, value };
}

/**
 * Stable short hash of the document's linkage so distinct-context documents with identical bytes
 * don't collapse. Empty when the document has no linkage (the common migration case), preserving
 * pure content-hash dedup there.
 */
function getDedupLinkageDiscriminator(request: CreateDocumentFromFileRequest): string {
  const relatedRefs = (request.context?.related ?? [])
    .map((related) => related.reference)
    .filter(
      (reference): reference is string => typeof reference === "string" && reference.length > 0,
    )
    .sort();
  // Fold in every coding code AND the free-text type: types defined by `text` only (e.g. Vital
  // LabLabels / CollectionInstructions) carry no coding, so keying on coding alone would let
  // different text-only types on the same order share a dedup key and collapse.
  const typeParts = [
    ...(request.type?.coding ?? [])
      .map((coding) => coding.code)
      .filter((code): code is string => typeof code === "string" && code.length > 0),
    ...(request.type?.text ? [`text:${request.type.text}`] : []),
  ].sort();
  const parts = [...relatedRefs, ...typeParts];
  if (parts.length === 0) {
    return "";
  }
  return hashFileContent(Buffer.from(parts.join("|"))).slice(0, 16);
}

/**
 * Appends the dedup-key identifier to any caller-provided identifiers, de-duping by system+value
 * so a caller that already set an equivalent identifier doesn't get a second copy.
 */
function mergeDedupKeyIdentifier(
  identifiers: SystemValuePair[] | undefined,
  dedupKeyIdentifier: SystemValuePair,
): SystemValuePair[] {
  const existing = identifiers ?? [];
  const alreadyPresent = existing.some(
    (identifier) =>
      identifier.system === dedupKeyIdentifier.system &&
      identifier.value === dedupKeyIdentifier.value,
  );
  return alreadyPresent ? existing : [...existing, dedupKeyIdentifier];
}

/**
 * Normalizes reference inputs into ResourceRef objects.
 */
function toReference(input: ResourceRef | string): ResourceRef {
  if (typeof input === "string") {
    return { reference: input };
  }

  return input;
}
